import React, { useEffect, useState } from 'react';
import {
  formatTimeZone,
  toUSCompactDateFormat,
} from '@common/services/datetime';
import { gql, useQuery, useMutation, useNavigate } from '@common/hooks';
import {
  AddButton,
  Button,
  Dropdown,
  ErrorHandler,
  FilteredTable,
  HistoryDropDown,
  Icon,
  Modal,
  Portal,
  Col,
  Row,
} from '@common/components';
import {
  ShiftTimesList as ShiftTimesListQuery,
  ShiftTimesListQueryVariables,
  ShiftTimesListQueryResponse,
} from '@scheduling/graphql/queries/organization-unit-shift-configs';
import {
  OrderDirection,
  ShiftTimesListOrderField,
} from '@common/graphql/schema';
import {
  DeleteOrganizationUnitShiftConfig,
  DeleteOrganizationUnitShiftConfigVariables,
  DeleteOrganizationUnitShiftConfigResponse,
} from '@scheduling/graphql/mutations/organization-unit-shift-config';
import { RolesQuery, RolesResponse } from '@clinician-profile/graphql/queries';
import { HistoryInfoFragment, HistoryInfoFragmentType } from '@common/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { OrganizationUnitShiftConfig } from '@scheduling/graphql/types';
import {
  WORKFORCE_GROUP_FLAT_QUERY,
  WorkforceGroupType,
} from '@scheduling/graphql';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
import { ShiftTimesDrawer } from '@scheduling/components';
import { buildDialogUrl } from '@common/services';

type SortableColumnKey = 'role' | 'displayTime' | 'lastShiftGapCreatedAt';

const COLUMN_KEY_TO_ORDER_FIELD: Record<
  SortableColumnKey,
  ShiftTimesListOrderField
> = {
  role: 'role',
  displayTime: 'display_time',
  lastShiftGapCreatedAt: 'last_shift_gap_created_at',
};

type SortState = {
  columnKey: SortableColumnKey;
  order: Exclude<SortOrder, null>;
} | null;

const OrganizationUnitShiftConfigInfoQuery = gql`
  query OrganizationUnitShiftConfigInfoQuery(
    $organizationUnitId: ID!
    $scope: String!
  ) {
    organizationUnit(uuid: $organizationUnitId, scope: $scope) {
      id
      shiftIngestionType
      canManageShiftConfigs {
        value
      }
      shiftConfigHistory {
        ...HistoryInfoFragment
      }
    }
  }
  ${HistoryInfoFragment}
`;

type OrganizationUnitShiftConfigInfoResponse = {
  organizationUnit: {
    id: string;
    shiftIngestionType: string;
    canManageShiftConfigs: { value: boolean };
    shiftConfigHistory: HistoryInfoFragmentType[];
  };
};

type OrganizationUnitShiftConfigInfoVariables = {
  organizationUnitId: string;
  scope: string;
};

type ShiftTimesListProps = {
  organizationUnitId: string;
  canEdit: boolean;
  triggerPortal?: boolean;
  integrationsEnabled?: string[];
};

export function formatLength(record: OrganizationUnitShiftConfig) {
  const length = Number(record.duration).toFixed(2);
  const unitFormatted = length === '1.00' ? 'h' : 'hrs';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div>{`${length} ${unitFormatted}`}</div>
    </div>
  );
}

export function ShiftTimesList({
  organizationUnitId,
  canEdit,
  triggerPortal,
  integrationsEnabled,
}: ShiftTimesListProps) {
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingShiftTime, setEditingShiftTime] =
    useState<OrganizationUnitShiftConfig | null>(null);
  const [removeShiftTimeLoading, setRemoveShiftTimeLoading] = useState(false);
  const [removeShiftId, setRemoveShiftId] = useState<string | null>(null);
  const [sortState, setSortState] = useState<SortState>(null);
  const {
    data: shiftTimesListData,
    error: shiftTimesListError,
    loading: shiftTimesListLoading,
    refetch: shiftTimesListRefetch,
    fetchMore: shiftTimesListFetchMore,
  } = useQuery<ShiftTimesListQueryResponse, ShiftTimesListQueryVariables>(
    ShiftTimesListQuery,
    {
      variables: {
        organizationUnitId,
      },
    }
  );

  const {
    data: rolesData,
    error: rolesError,
    loading: rolesLoading,
  } = useQuery<RolesResponse>(RolesQuery);

  const {
    data: workforceGroupData,
    error: workforceGroupError,
    loading: workforceGroupLoading,
  } = useQuery(WORKFORCE_GROUP_FLAT_QUERY);

  const {
    data: shiftTimesInfoData,
    error: shiftTimesHistoryError,
    refetch: shiftTimesHistoryRefetch,
  } = useQuery<
    OrganizationUnitShiftConfigInfoResponse,
    OrganizationUnitShiftConfigInfoVariables
  >(OrganizationUnitShiftConfigInfoQuery, {
    variables: {
      organizationUnitId,
      scope: 'organization',
    },
  });

  const [deleteOrganizationUnitShiftConfig] = useMutation<
    DeleteOrganizationUnitShiftConfigResponse,
    DeleteOrganizationUnitShiftConfigVariables
  >(DeleteOrganizationUnitShiftConfig);
  const canManageShiftTimes =
    shiftTimesInfoData?.organizationUnit?.canManageShiftConfigs?.value ?? false;
  const canEditShiftTimes = canEdit && canManageShiftTimes;

  const isShiftSets =
    shiftTimesInfoData?.organizationUnit?.shiftIngestionType === 'shift_sets';

  useEffect(() => {
    if (!shiftTimesInfoData || sortState) return;

    setSortState(
      isShiftSets
        ? { columnKey: 'lastShiftGapCreatedAt', order: 'descend' }
        : { columnKey: 'role', order: 'ascend' }
    );
  }, [shiftTimesInfoData, isShiftSets, sortState]);

  function handleRefetch() {
    shiftTimesListRefetch();
    shiftTimesHistoryRefetch();
  }

  async function onRemove(id: string) {
    try {
      const response = await deleteOrganizationUnitShiftConfig({
        variables: { id },
      });

      if (response?.errors?.length) {
        const messages = response?.errors?.map(({ message }) => message);
        createMessage('error', messages?.join(', '));
      } else {
        onRemoveSuccess();
      }
    } catch {
      createMessage(
        'error',
        "Shift Time couldn't be removed at this time. Try again later or contact support."
      );
    }
  }

  function onRemoveSuccess() {
    createMessage('success', 'Shift Time successfully removed.');
    handleRefetch();
  }

  function handleTableChange(
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter:
      | SorterResult<OrganizationUnitShiftConfig>
      | SorterResult<OrganizationUnitShiftConfig>[]
  ) {
    const active = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSort = normalizeSort(active);
    setSortState(nextSort);

    const orderVariables = buildOrderVariables(nextSort);

    const variables: ShiftTimesListQueryVariables = {
      organizationUnitId,
      limit: pagination.pageSize,
      offset:
        pagination.current && pagination.pageSize
          ? (pagination.current - 1) * pagination.pageSize
          : 0,
      ...orderVariables,
    };

    shiftTimesListFetchMore({
      variables,
      updateQuery: (_prev, { fetchMoreResult }) => {
        return fetchMoreResult;
      },
    });
  }

  function normalizeSort(
    sorter?: SorterResult<OrganizationUnitShiftConfig>
  ): SortState {
    if (!sorter?.order) return null;
    const columnKey = sorter.columnKey as SortableColumnKey | undefined;
    if (!columnKey || !(columnKey in COLUMN_KEY_TO_ORDER_FIELD)) return null;
    return { columnKey, order: sorter.order };
  }

  function buildOrderVariables(next: SortState) {
    if (!next) return {};
    return {
      orderField: COLUMN_KEY_TO_ORDER_FIELD[next.columnKey],
      orderDirection: (next.order === 'descend'
        ? 'DESC'
        : 'ASC') as OrderDirection,
    };
  }

  function sortOrderFor(columnKey: SortableColumnKey): SortOrder {
    return sortState?.columnKey === columnKey ? sortState.order : null;
  }

  function onConfirmRemove() {
    setRemoveShiftTimeLoading(true);

    onRemove(removeShiftId || '').then(() => {
      setRemoveShiftId(null);
      setRemoveShiftTimeLoading(false);
    });
  }

  const columns = [
    {
      key: 'role',
      dataIndex: 'role',
      title: 'Role',
      width: '30%',
      render: (role: { id?: string; abbreviation?: string }) =>
        role ? role?.abbreviation : 'Default',
      sorter: true,
      sortOrder: sortOrderFor('role'),
    },
    {
      key: 'workforceGroups',
      dataIndex: 'workforceGroups',
      title: 'Group',
      width: '30%',
      render: (workforceGroups: WorkforceGroupType[]) =>
        workforceGroups
          ?.map((workforceGroup) => workforceGroup?.name.trimEnd())
          ?.join(', ') || 'All',
    },
    {
      key: 'displayTime',
      dataIndex: 'displayTime',
      title: 'Display Time',
      render: (_: string, record: OrganizationUnitShiftConfig) => {
        return `${formatTimeZone(record.startTime)} - ${formatTimeZone(
          record.endTime
        )}`;
      },
      sorter: true,
      sortOrder: sortOrderFor('displayTime'),
    },
    {
      key: 'workloadTime',
      dataIndex: 'workloadTime',
      title: 'Workload Settings',
      render: (_: string, record: OrganizationUnitShiftConfig) => {
        if (!record.workloadStartTime || !record.workloadEndTime) {
          return '-';
        }

        return `${formatTimeZone(record.workloadStartTime)} - ${formatTimeZone(
          record.workloadEndTime
        )}`;
      },
    },
    {
      key: 'length',
      dataIndex: 'length',
      title: 'Length',
      render: (_: string, record: OrganizationUnitShiftConfig) =>
        formatLength(record),
    },
    {
      key: 'lastShiftGapCreatedAt',
      dataIndex: 'lastShiftGapCreatedAt',
      title: 'Last Shift Created',
      render: (_: string, record: OrganizationUnitShiftConfig) =>
        record.lastShiftGapCreatedAt
          ? toUSCompactDateFormat(record.lastShiftGapCreatedAt)
          : '—',
      sorter: true,
      sortOrder: sortOrderFor('lastShiftGapCreatedAt'),
    },
    {
      key: 'actions',
      dataIndex: 'actions',
      title: 'Actions',
      width: '5%',
      render: (_: string, record: OrganizationUnitShiftConfig) => {
        const items = [
          {
            key: 'edit',
            label: 'Edit',
            onClick: () => {
              setEditingShiftTime(record);
              setDrawerOpen(true);
            },
          },
          {
            key: 'remove',
            label: 'Remove',
            onClick: () => setRemoveShiftId(record.id),
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button
              id="shift-time-actions"
              kind="link"
              shape="circle"
              ariaLabel="Send Shift"
              icon={
                <Icon
                  size="lg"
                  icon="verticalEllipsis"
                  kind="regular"
                  color="elm"
                />
              }
            />
          </Dropdown>
        );
      },
    },
  ]
    .filter((column) => column.key !== 'actions' || canEditShiftTimes)
    .filter((column) => column.key !== 'lastShiftGapCreatedAt' || isShiftSets);

  const errors =
    shiftTimesListError ||
    rolesError ||
    shiftTimesHistoryError ||
    workforceGroupError;
  if (errors) {
    return <ErrorHandler error={errors} log="ShiftTimesListError" />;
  }

  const shiftTimes =
    shiftTimesListData?.shiftTimesList.organizationUnitShiftConfigs;

  const copyShiftTimesItems = [
    {
      key: 'from',
      label: 'From Unit',
    },
    ...(shiftTimes && shiftTimes?.length > 0
      ? [
          {
            key: 'to',
            label: 'To Units',
          },
        ]
      : []),
  ];

  const onCopyShiftTimeItemsClick = (item: { key: string }) => {
    const type = item.key;

    const shiftTimeDrawerUrl = buildDialogUrl('copy-shift-times-drawer', {
      props: {
        type,
        unitId: organizationUnitId,
      },
    });

    navigate(shiftTimeDrawerUrl);
  };

  return (
    <>
      {canEditShiftTimes && (
        <Portal
          targetId="wk-portal-target:location-shift-times-list-extra-actions"
          triggerRender={triggerPortal}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'space-between',
              gap: 16,
              marginTop: 0,
              marginBottom: 10,
            }}
          >
            <Dropdown
              menu={{
                items: copyShiftTimesItems,
                onClick: onCopyShiftTimeItemsClick,
              }}
              className="add-staff-dropdown"
              placement="bottomRight"
            >
              <Button
                size="md"
                icon={<Icon icon="copy" kind="light" />}
                iconRight={
                  <Icon
                    icon="chevronDown"
                    size="sm"
                    kind="regular"
                    color="steelDarker"
                  />
                }
              >
                Copy Shift Times
              </Button>
            </Dropdown>
            <AddButton
              onClick={() => {
                setEditingShiftTime(null);
                setDrawerOpen(true);
              }}
            >
              Shift Time
            </AddButton>
          </div>
        </Portal>
      )}
      <ShiftTimesDrawer
        onClose={() => {
          setEditingShiftTime(null);
          setDrawerOpen(false);
        }}
        organizationUnitId={organizationUnitId}
        handleRefetch={handleRefetch}
        open={drawerOpen}
        loadingData={rolesLoading || workforceGroupLoading}
        workforceGroups={workforceGroupData?.workforceGroups || []}
        roles={rolesData?.roles}
        shiftIngestionType={
          shiftTimesInfoData?.organizationUnit?.shiftIngestionType
        }
        integrationsEnabled={integrationsEnabled}
        shiftTime={editingShiftTime}
      />
      <Row>
        <Col span={24}>
          <FilteredTable
            className="admin-organization-unit-view__shift-times__table"
            rowKey="id"
            loading={shiftTimesListLoading}
            columns={columns}
            dataSource={shiftTimes}
            onChange={handleTableChange}
            pagination={{
              total: shiftTimesListData?.shiftTimesList?.totalCount,
            }}
            localStorageFilterKey={undefined}
            onFilterChange={undefined}
            filters={undefined}
            scroll={{ x: '100%' }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <HistoryDropDown
            data={
              shiftTimesInfoData?.organizationUnit?.shiftConfigHistory || []
            }
            hrVisible={false}
          />
        </Col>
      </Row>
      <Modal
        title="Are you sure you want to remove this shift time?"
        visible={removeShiftId !== null}
        onCancel={() => setRemoveShiftId(null)}
        footer={
          <>
            <Button key="back" onClick={() => setRemoveShiftId(null)}>
              Back
            </Button>
            <Button
              danger
              key="submit"
              loading={removeShiftTimeLoading}
              onClick={onConfirmRemove}
            >
              Remove
            </Button>
          </>
        }
      >
        All shifts associated with this shift time will be removed. Previously
        claimed shifts will not be impacted. This action cannot be undone.
      </Modal>
    </>
  );
}
